import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, api } from '../services/api';
import toast from 'react-hot-toast';
import { AUTH_TOKEN_KEY, USER_DATA_KEY } from '../constants';
import { setupAxiosInterceptors } from '../utils/axiosConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Define clearAuth early so it can be used in useEffect
  const clearAuth = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Setup axios interceptors for 401 errors
  useEffect(() => {
    setupAxiosInterceptors(api, () => {
      // On 401 error, clear auth state
      clearAuth();
      toast.error('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar token al cargar la aplicación
  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const userData = localStorage.getItem(USER_DATA_KEY);

      if (!token) {
        setIsLoading(false);
        return;
      }

      // v3.0.0: Token legacy detection disabled
      // The verify-token endpoint now returns all v3.0.0 fields correctly
      // So we don't need to check localStorage anymore - we verify with the API instead

      // Verificar si el token aún es válido
      const response = await authAPI.verifyToken();

      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        // Actualizar datos del usuario en localStorage
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.data.user));
      } else {
        // Token inválido, limpiar
        clearAuth();
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login({ email, password });
      
      if (response.data.success) {
        const { token, user: userData } = response.data.data;
        
        // Guardar en localStorage
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        
        // Actualizar estado
        setUser(userData);
        setIsAuthenticated(true);

        // Use full_name or email as fallback
        const displayName = userData.full_name || userData.email || 'Usuario';
        toast.success(`¡Bienvenido, ${displayName}!`);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error al iniciar sesión';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);

      if (response.data.success) {
        toast.success('Registro exitoso. Revisa tu email para confirmar tu cuenta.');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error en el registro';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (token, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.verifyEmail({ token, password });
      
      if (response.data.success) {
        toast.success('Email confirmado exitosamente. Ya puedes iniciar sesión.');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error en la verificación';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setIsLoading(true);
      const response = await authAPI.forgotPassword({ email });
      
      if (response.data.success) {
        toast.success('Si el email existe, recibirás instrucciones para resetear tu contraseña.');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error al solicitar cambio de contraseña';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.resetPassword({ token, password });
      
      if (response.data.success) {
        toast.success('Contraseña actualizada exitosamente.');
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Error al resetear la contraseña';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Intentar logout en el servidor
      await authAPI.logout();
    } catch (error) {
      // Continuar con logout local incluso si falla el servidor
      console.error('Error en logout del servidor:', error);
    } finally {
      // Limpiar estado local
      clearAuth();
      toast.success('Sesión cerrada correctamente');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    isAdmin: user?.role_id === 1, // v3.0.0: Only Super Admin (role_id = 1) is admin
    login,
    register,
    verifyEmail,
    forgotPassword,
    resetPassword,
    logout,
    updateUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;